/**
 * Proprietary SOL to USD conversion system.
 *
 * Owns every SOL to USD conversion in the app. The public API intentionally
 * separates "live" and "locked" conversions so it is impossible to mix them:
 *
 *   LIVE    requires a currentSolPriceUsd argument and is used only for values
 *           that represent current market state (market cap, FDV, pool
 *           liquidity, mark to market user positions, in flight quotes).
 *
 *   LOCKED  takes NO price argument. It reads a historically locked USD value
 *           that was stored by the backend at the moment of accrual (see
 *           supabase/functions/sync-pool-stats). Used for any quantity that
 *           must NOT move with SOL price: total creator fees earned, claimed
 *           fees, ATH USD, etc.
 *
 * Do not add helpers that take both a historical SOL value and a live price as
 * arguments. That API shape caused the creator fees inflation bug this module
 * was built to fix.
 */

import { formatCurrency } from '@/lib/utils/formatters';

// region Live conversions

/**
 * Mark to market a SOL amount using the CURRENT SOL price.
 * Use ONLY for values that represent current market state
 * (market cap, FDV, pool liquidity, user position, quotes).
 * NEVER use for historical or accrued values.
 */
export function convertSolToUsdLive(
  solAmount: number | null | undefined,
  currentSolPriceUsd: number,
): number | null {
  if (solAmount == null || isNaN(solAmount) || solAmount < 0) return null;
  if (!Number.isFinite(currentSolPriceUsd) || currentSolPriceUsd <= 0) return null;
  return solAmount * currentSolPriceUsd;
}

/**
 * Format a live SOL to USD conversion. Returns '--' when either input is invalid.
 */
export function formatLiveUsd(
  solAmount: number | null | undefined,
  currentSolPriceUsd: number,
): string {
  const usd = convertSolToUsdLive(solAmount, currentSolPriceUsd);
  return usd == null ? '--' : formatCurrency(usd);
}

// endregion

// region Locked conversions

/**
 * Shape of the token row needed to read locked creator fees USD.
 * Both fields are nullable because the 1 minute sync may not have populated
 * them yet on a freshly created pool.
 */
export interface LockedCreatorFeesInput {
  cumulativeCreatorEarningsUsd: number | null | undefined;
  dammV2CumulativeFeeUsd: number | null | undefined;
  isGraduated: boolean;
}

/**
 * Return the historically locked creator fees USD for a token.
 *
 * Pre graduation the value comes from `pool_stats.cumulative_creator_earnings_usd`.
 * Post graduation (DAMM V2) the total is the sum of the pre graduation locked
 * total plus the `pool_stats.damm_v2_cumulative_fee_usd` accrued after migration.
 *
 * Returns null when no locked value is available yet. The caller should render
 * '--' in that case and MUST NOT fall back to a live multiplication, since
 * that would recreate the bug this utility was built to prevent.
 */
export function getLockedCreatorFeesUsd(
  token: LockedCreatorFeesInput,
): number | null {
  const bondingUsd = sanitizeLocked(token.cumulativeCreatorEarningsUsd);
  const dammUsd = sanitizeLocked(token.dammV2CumulativeFeeUsd);

  // Post graduation: sum of pre and post migration locked totals.
  if (token.isGraduated) {
    if (bondingUsd == null && dammUsd == null) return null;
    return (bondingUsd ?? 0) + (dammUsd ?? 0);
  }

  // Pre graduation: only the bonding curve cumulative.
  return bondingUsd;
}

/**
 * Format locked creator fees USD for display.
 * Returns '--' when no locked value is available.
 */
export function formatLockedCreatorFeesUsd(
  token: LockedCreatorFeesInput,
): string {
  const usd = getLockedCreatorFeesUsd(token);
  return usd == null ? '--' : formatCurrency(usd);
}

// endregion

function sanitizeLocked(value: number | null | undefined): number | null {
  if (value == null) return null;
  if (!Number.isFinite(value)) return null;
  if (value < 0) return null;
  return value;
}
